// @noble/ed25519 + @noble/hashes are dynamically imported inside
// verifyLicense() so they don't land in the initial bundle. License
// verification is a rare action — every page load only needs isPro(),
// which is a synchronous localStorage read.

/**
 * Ed25519 public key bundled with the app, in hex. The matching private
 * key lives only as a Cloudflare Worker secret.
 *
 * ⚠️ LAUNCH-BLOCKER: this is a development keypair shared in this
 * commit history. BEFORE deploying to production:
 *   1. Generate a fresh keypair: `node -e "const k=require('@noble/ed25519');
 *      (async()=>{const p=k.utils.randomPrivateKey();
 *      console.log('priv',Buffer.from(p).toString('hex'));
 *      console.log('pub ',Buffer.from(await k.getPublicKey(p)).toString('hex'))})()"`
 *   2. Replace this constant with the new pub key
 *   3. Set the worker's LICENSE_PRIVATE_KEY_HEX secret to the new priv key
 *   4. Existing licenses minted under the dev keypair stop validating
 *      (intended — those were test grants).
 */
export const LICENSE_PUBLIC_KEY_HEX =
  '6d589c8ede9d3a921ebbda0933edc6b66659319556d914482cf86a7d6d7cdd8d';

/**
 * URL of the deployed license worker. Handles Stripe webhooks +
 * per-license device-activation tracking. Override per environment
 * with VITE_LICENSE_WORKER_URL in .env so staging / prod don't need
 * a code change to point at a different worker.
 */
export const LICENSE_WORKER_URL =
  import.meta.env.VITE_LICENSE_WORKER_URL ??
  'https://anomia-license.seanfoleydesigns.workers.dev';

const STORAGE_KEY = 'anomia:license';
const DEVICE_ID_KEY = 'anomia:device-id';

export type LicensePayload = {
  email: string;
  purchasedAt: string;
  version: 1;
};

export type StoredLicense = LicensePayload & {
  /** The full encoded license string the user pasted in. */
  raw: string;
};

const hexToBytes = (hex: string): Uint8Array => {
  const clean = hex.toLowerCase();
  if (!/^[0-9a-f]*$/.test(clean) || clean.length % 2 !== 0) {
    throw new Error('invalid hex');
  }
  const bytes = new Uint8Array(clean.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
};

const b64UrlDecode = (s: string): Uint8Array => {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + pad).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
};

const isPayload = (v: unknown): v is LicensePayload => {
  if (!v || typeof v !== 'object') return false;
  const p = v as Partial<LicensePayload>;
  return (
    typeof p.email === 'string' &&
    typeof p.purchasedAt === 'string' &&
    p.version === 1
  );
};

/**
 * Verify a license string and return the payload if valid.
 * Format: <base64url-payload>.<base64url-signature>
 */
export const verifyLicense = async (raw: string): Promise<LicensePayload | null> => {
  const trimmed = raw.trim();
  const dot = trimmed.indexOf('.');
  if (dot <= 0 || dot === trimmed.length - 1) return null;
  const payloadB64 = trimmed.slice(0, dot);
  const sigB64 = trimmed.slice(dot + 1);

  let payloadBytes: Uint8Array;
  let sig: Uint8Array;
  let pub: Uint8Array;
  try {
    payloadBytes = b64UrlDecode(payloadB64);
    sig = b64UrlDecode(sigB64);
    pub = hexToBytes(LICENSE_PUBLIC_KEY_HEX);
  } catch {
    return null;
  }

  const ed = await import('@noble/ed25519');
  const { sha512 } = await import('@noble/hashes/sha2.js');
  ed.hashes.sha512 = sha512;
  let ok: boolean;
  try {
    ok = await ed.verifyAsync(sig, payloadBytes, pub);
  } catch {
    // wrong-sized sig / pub bytes throw RangeError — treat as invalid
    return null;
  }
  if (!ok) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(payloadBytes));
  } catch {
    return null;
  }
  return isPayload(parsed) ? parsed : null;
};

const readStored = (): StoredLicense | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredLicense>;
    if (
      isPayload(parsed) &&
      typeof (parsed as StoredLicense).raw === 'string'
    ) {
      return parsed as StoredLicense;
    }
  } catch {
    /* ignore */
  }
  return null;
};

const writeStored = (license: StoredLicense): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(license));
  } catch {
    /* localStorage may be unavailable */
  }
};

export type ActivationResult =
  | { ok: true; payload: LicensePayload; deviceCount: number; max: number }
  | { ok: false; reason: 'invalid' }
  | { ok: false; reason: 'limit-reached'; deviceCount: number; max: number }
  | { ok: false; reason: 'network' };

/** Stable per-browser device identifier. Generated on first activation
 *  and persisted; clearing localStorage counts as a "new device" against
 *  the per-license cap, which is the trade-off we accept for not
 *  fingerprinting users any harder. */
const ensureDeviceId = (): string => {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing && existing.length >= 8) return existing;
  } catch {
    /* fall through */
  }
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  try {
    localStorage.setItem(DEVICE_ID_KEY, id);
  } catch {
    /* ignore */
  }
  return id;
};

/**
 * Verify a license, register this device with the worker, and persist
 * on success. The remote step counts this device against the per-
 * license cap (default 5). Without a successful remote registration
 * the license is *not* persisted — without it the worker won't accept
 * the device on subsequent purchases.
 */
export const activateLicense = async (raw: string): Promise<ActivationResult> => {
  const trimmed = raw.trim();
  // Fast offline reject so we don't bother the worker for obvious junk.
  const payload = await verifyLicense(trimmed);
  if (!payload) return { ok: false, reason: 'invalid' };

  const deviceId = ensureDeviceId();
  let resp: Response;
  try {
    resp = await fetch(`${LICENSE_WORKER_URL}/activate`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ license: trimmed, deviceId }),
    });
  } catch {
    return { ok: false, reason: 'network' };
  }

  if (resp.status === 403) {
    let body: { deviceCount?: number; max?: number } = {};
    try {
      body = (await resp.json()) as typeof body;
    } catch {
      /* ignore */
    }
    return {
      ok: false,
      reason: 'limit-reached',
      deviceCount: typeof body.deviceCount === 'number' ? body.deviceCount : 0,
      max: typeof body.max === 'number' ? body.max : 5,
    };
  }
  if (!resp.ok) {
    return { ok: false, reason: 'network' };
  }
  let body: { deviceCount?: number; max?: number } = {};
  try {
    body = (await resp.json()) as typeof body;
  } catch {
    /* ignore */
  }
  writeStored({ ...payload, raw: trimmed });
  return {
    ok: true,
    payload,
    deviceCount: typeof body.deviceCount === 'number' ? body.deviceCount : 1,
    max: typeof body.max === 'number' ? body.max : 5,
  };
};

export const removeLicense = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

export const storedLicense = (): StoredLicense | null => readStored();

/**
 * Synchronous Pro check used at every entry point. Returns true iff a valid
 * license is in localStorage. We don't re-verify on every check; activation
 * already ran the signature check, and tampering with localStorage only
 * grants Pro on the user's own machine.
 */
export const isPro = (): boolean => readStored() !== null;
